import * as React from 'react'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  adminGetPlanFeatureSettingsAdminPlanFeatureSettingsGetOptions,
  adminSavePlanFeatureSettingsAdminPlanFeatureSettingsPostMutation,
} from '@/generated/@tanstack/react-query.gen'
import { get } from 'lodash'
import {
  convertArrayToPricingData,
  convertPricingDataToArray,
} from './_data/helper'
import {
  FeatureWithPlans,
  planKeys,
  plans,
  PricingData,
} from './_data/feature-matrix.interface'
import FullScreenLoader from '@/components/full-screen-loader'
import { toast } from '@/components/custom-toast'
import Spinner from '@/components/Spinner'
import isFeatureDisabled from './_data/feature-dependencies'

export default function FeatureMatrix() {
  const [enableButton, setEnableButton] = React.useState(false)
  const { data, isLoading, refetch } = useQuery({
    ...adminGetPlanFeatureSettingsAdminPlanFeatureSettingsGetOptions(),
    select: (data) =>
      convertPricingDataToArray(
        get(data, 'settings', {}) as PricingData,
        get(data, 'labels', {})
      ),
  })

  const savePlanMutation = useMutation({
    ...adminSavePlanFeatureSettingsAdminPlanFeatureSettingsPostMutation(),
    onError: (error) => {
      toast.error({
        title: 'Error',
        message:
          (error.response?.data.detail as unknown as string) ?? error.message,
      })
    },
    onSuccess: () => {
      refetch()
      toast.success({
        title: 'Success',
        message: 'Plan settings saved successfully.',
      })
    },
  })

  const [features, setFeatures] = React.useState<FeatureWithPlans[]>([])

  React.useEffect(() => {
    if (data) {
      setFeatures(data.features)
    }
  }, [data])

  const handleFeatureChange = (
    featureIndex: number,
    planIndex: number,
    number?: number
  ) => {
    setFeatures((prev) =>
      prev.map((feature, index) =>
        index === featureIndex
          ? {
              ...feature,
              plans: {
                ...feature.plans,
                [planKeys[planIndex]]:
                  number !== undefined
                    ? number
                    : !feature.plans[planKeys[planIndex]],
              },
            }
          : feature
      )
    )
  }

  if (isLoading) return <FullScreenLoader />

  return (
    <div className="flex flex-col h-full">
      {savePlanMutation.isPending && <Spinner />}
      <div className="w-full flex items-center justify-between text-black text-m font-medium rounded transition duration-150 ease-in-out mt-2 mb-2 p-6">
        <div>Feature Matrix</div>
        <div className="flex space-x-4">
          <button
            onClick={() => {
              const payload = convertArrayToPricingData(features)
              savePlanMutation.mutate({
                body: {
                  settings: { ...payload.data },
                },
              })
            }}
            className={`${
              enableButton
                ? 'bg-[#007377] hover:bg-[#01589d] focus:bg-[#01589d] cursor-pointer'
                : 'bg-gray-600 hover:bg-gray-700 focus:bg-gray-700 cursor-not-allowed'
            } text-white font-medium text-sm rounded shadow-md hover:shadow-lg focus:shadow-lg w-[270px] text-center transition duration-150 ease-in-out`}
            disabled={!enableButton}
          >
            Save Changes
          </button>
        </div>
      </div>
      <div className="min-h-[100px] max-h-[700px] justify-between items-center m-[10px] overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[250px]"></TableHead>
              {plans.map((plan) => (
                <TableHead key={plan} className="text-center">
                  <h6 className="font-bold">{plan}</h6>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {features.map((feature, featureIndex) => (
              <TableRow key={feature.key}>
                <TableCell className="font-medium h-[64px]">
                  <p className="pl-3">
                    {get(data, ['labels', feature.key], feature.key)}
                  </p>
                </TableCell>
                {planKeys.map((planKey, planIndex) => {
                  const isBoolean = typeof feature.plans[planKey] === 'boolean'
                  const isNoLimit = feature.plans[planKey] === -1
                  const isNumber = typeof feature.plans[planKey] === 'number'

                  if (
                    planKey === 'enterprise' &&
                    feature.key === 'number_of_reports_per_month'
                  ) {
                    return (
                      <TableCell
                        key={planIndex}
                        className="text-center !px-4"
                      />
                    )
                  }
                  const disableUpdate = isFeatureDisabled(
                    feature.key,
                    planKey,
                    features
                  )

                  return (
                    <TableCell key={planIndex} className="text-center !px-4">
                      {isBoolean && (
                        <Checkbox
                          checked={Boolean(feature.plans[planKey])}
                          onCheckedChange={() => {
                            setEnableButton(true)
                            return handleFeatureChange(featureIndex, planIndex)
                          }}
                          className="mx-auto"
                        />
                      )}

                      {isNumber && (
                        <div className="w-full flex flex-col items-center">
                          <div className="flex flex-col space-y-3 w-[76px]">
                            <Input
                              type="number"
                              value={
                                typeof feature.plans[planKey] === 'number'
                                  ? feature.plans[planKey] === -1
                                    ? 0
                                    : feature.plans[planKey]
                                  : 0
                              }
                              onChange={(e) => {
                                setEnableButton(true)
                                return handleFeatureChange(
                                  featureIndex,
                                  planIndex,
                                  parseInt(e.target.value)
                                )
                              }}
                              className="mx-auto w-full disabled:bg-[#D8D8D899]"
                              disabled={
                                feature.plans[planKey] === -1 || disableUpdate
                              }
                            />
                            <div className="flex flex-row items-center space-x-2">
                              <Checkbox
                                id={`${planKey}-${featureIndex}`}
                                disabled={disableUpdate}
                                checked={isNoLimit}
                                onCheckedChange={(checked) => {
                                  setEnableButton(true)
                                  return handleFeatureChange(
                                    featureIndex,
                                    planIndex,
                                    checked ? -1 : 0
                                  )
                                }}
                              />
                              <label
                                htmlFor={`${planKey}-${featureIndex}`}
                                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                              >
                                No limit
                              </label>
                            </div>
                          </div>
                        </div>
                      )}
                    </TableCell>
                  )
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
